<template>
  <b-tabs>
    <b-tab-item
      v-if="trialMode"
      label="Free trial"
    >
      <p class="has-text-weight-bold">
        What does the trial allow?
      </p>
      <p>
        You're free to publish content on your channels (or client channels) depending on
        the pricing tier you have selected. You have full access to the Sonata catalogue
        during your trial.
      </p>
      <br>

      <p class="has-text-weight-bold">
        If I use a track in some published work, will the license still be valid if I
        cancel the trial?
      </p>
      <p>
        Your licenses will still be valid forever, even if you cancel your free trial
        - so long as the content was published during the free trial period.
      </p>
      <br>

      <p class="has-text-weight-bold">
        What is not allowed?
      </p>
      <p>
        Tracks cannot be used for content published after the free trial if subscription
        is cancelled. If you publish the content the track is used on during the trial
        period, then you're covered.
      </p>
      <br>

      <p class="has-text-weight-bold">
        What happens at the end of the free trial?
      </p>
      <p>
        The trial will automatically renew at the subscription price for your plan,
        unless cancelled during the 30-day window.
      </p>
      <br>

      <p class="has-text-weight-bold">
        How do I cancel my free trial?
      </p>
      <p>
        You can cancel your trial at any time, super-easily with a few clicks, within
        the "Subscription" tab of your account.
      </p>
      <br>
    </b-tab-item>

    <b-tab-item label="License">
      <p class="has-text-weight-bold">
        How long will my license be valid for?
      </p>
      <p>
        Forever, even if your subscription ends. When you download a song or sound under
        your Sonata subscription, you're licensed to use it in your project in perpetuity
        - which means, until the end of time itself. The license doesn't end if your
        subscription ends.
      </p>
      <br>

      <p class="has-text-weight-bold">
        I want to subscribe as a business - will that cover my employees and collaborators?
      </p>
      <p>
        Yes. If your business subscribes to Sonata, then any employees can access the music
        to use in company projects. Should an employee want to use music for their own
        private project, then they would have to subscribe to our Personal Plan.
      </p>
      <br>

      <p class="has-text-weight-bold">
        If a client hires me for a video project, will they be covered by my license?
      </p>
      <p>
        Absolutely. If you create a film for a client and use music from Sonata, your
        license will cover their commercial use of that video. As it only covers that
        specific video, it means they can't use the music for a different production
        that you haven't worked on.
      </p>
      <br>

      <p class="has-text-weight-bold">
        Do I have to pay extra if I'm using it for paid and branded advertising?
      </p>
      <p>
        Not if you're paying for our Commercial or Extended plan. You're not covered
        for paid ads under our Personal tier.
      </p>
      <br>

      <p class="has-text-weight-bold">
        Does my license cover content outside of video production?
      </p>
      <p>
        Yes your license doesn't just cover video production, but also games, podcasts
        and radio (depending on your tier).
      </p>
      <br>

      <p class="has-text-weight-bold">
        Do I need to credit Sonata in the content I publish?
      </p>
      <p>
        No, there is no requirement to credit us or artists at all, providing you have an
        active license or trial when the content is published. If you do want to show some
        love though, it will be much appreciated!
      </p>
      <br>

      <p class="has-text-weight-bold">
        Can I do remixes and covers of Sonata music?
      </p>
      <p>
        No unfortunately you may not - our licenses do not cover it. Our music is intended
        to be use to support your video or podcast work - not to be used to create new
        music - so you may cut and loop our tracks to your heart's content, but please don't
        remix or cover any of our songs and upload them to places such as Spotify or SoundCloud.
      </p>
      <br>
      <p>
        If you are really in love with one of our pieces and want to use the music in a remix
        or cover, please get in touch with us and we may be able to help.
      </p>
      <br>
    </b-tab-item>

    <b-tab-item label="Claims">
      <p class="has-text-weight-bold">
        What is Content ID?
      </p>
      <p>
        Content ID is a digital fingerprinting system developed by Google which is used to
        easily identify and manage copyrighted content on YouTube. Videos uploaded to YouTube are compared against audio and video files registered with Content ID by content owners, looking for any matches It prevents fraudulent and unlicensed use of Sonata music. It keeps you as a creator safe, and our artists too.
      </p>
      <br>

      <p class="has-text-weight-bold">
        How does it work?
      </p>
      <p>
        When you upload your project onto platforms like Youtube, Facebook, Instagram etc,
        they use a system that scans and identifies our music. By placing a "Content ID
        claim" on the song that's been used, it acknowledges the artist behind the song.
      </p>
      <br>

      <p class="has-text-weight-bold">
        What does it mean if I get a Content ID claim?
      </p>
      <p>
        Don't be alarmed - none of your work will be taken down and it doesn't negatively
        affect your account in any way. Content ID technology is still relatively new and
        platforms like Youtube are still adapting it to suit the ever-changing digital realm.
        If you do receive a claim, simply drop us a line at
        <a href="mailto:support@sonata.media">support@sonata.media</a> and we can help.
      </p>
      <br>

      <p class="has-text-weight-bold">
        Is Sonata's music cleared for YouTube and monetisation?
      </p>
      <p>
        Yes! Most of our music has been precleared and we're currently working on a solution
        that will automatically clear claims with a linked YouTube account.
      </p>
      <br>
    </b-tab-item>

    <b-tab-item label="Billing">
      <p class="has-text-weight-bold">
        Can I get a refund?
      </p>
      <p>
        Sure, if you would like a full refund within the first 14 days of your subscription
        (and haven't downloaded any tracks) then we can take care of that for you. Just email
        <a href="mailto:support@sonata.media">support@sonata.media</a>. You can also cancel
        your annual subscription renewal at any time in the "Subscription" tab within your
        account.
      </p>
      <br>

      <p class="has-text-weight-bold">
        Will I get an invoice when I purchase a subscription?
      </p>
      <p>
        You can find invoices for subscriptions and single purchases in the "Invoices" tab in
        your account.
      </p>
      <br>
    </b-tab-item>
  </b-tabs>
</template>

<script>
export default {
  name: 'FAQList',

  computed: {
    trialMode: function () {
      return this.$store.getters['subscriptions/createSubscriptionWithTrial']
    }
  }
}
</script>
